import { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  Cpu,
  Info,
  Bell,
  Tags,
  Gauge,
  Inbox,
  Link2,
  Music,
  Radio,
  Users,
  Video,
  Waves,
  IdCard,
  Factory,
  Headset,
  Network,
  Package,
  UserCog,
  FileDown,
  BookText,
  Activity,
  ScanFace,
  BellRing,
  RotateCcw,
  LineChart,
  ScanSearch,
  HeartPulse,
  Smartphone,
  BadgeCheck,
  CreditCard,
  FolderOpen,
  LayoutGrid,
  Headphones,
  UserSearch,
  ShieldCheck,
  ChevronDown,
  DatabaseZap,
  FlaskConical,
  UserRoundCog,
  CalendarCheck,
  ClipboardList,
  TriangleAlert,
  MessagesSquare,
  MessageSquareText,
  Image as ImageIcon,
} from 'lucide-react';

import { cn } from '../../lib/cn';
import { Badge } from '../../ui/Badge';
import { paths } from '../../routes/paths';
import { APP_VERSION } from '../../lib/appVersion';
import { selectUser } from '../../store/selectors/auth';
import { AboutDialog } from '../../components/AboutDialog';
import { preloadRoute } from '../../routes/sections/dashboard';
import { SUPPORT_EMAIL, supportMailto } from '../../lib/support';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { getReviewSummaryAction } from '../../store/slices/review';
import { selectReviewSummary } from '../../store/selectors/review';
import {
  canManagePlans,
  canManageCache,
  canReadContent,
  canReadFirmware,
  canManageDevices,
  canViewCustomers,
  canViewBiosignal,
  canWorkCoachChat,
  canManageFirmware,
  canViewProduction,
  canWorkSupportChat,
  canRunSupportConsole,
  canManageAppVersions,
  canManageAssessments,
  canViewCoachInsights,
  canManageSleepCoaches,
  canManageEntitlements,
  canManageNotifications,
  canManageActivityGroups,
} from '../../lib/roles';

const iconFor = (slug) => {
  switch (slug) {
    case 'content-taxonomy':
      return <Network className="h-4 w-4" />;
    case 'content-attributes':
      return <Tags className="h-4 w-4" />;
    case 'session-audio':
      return <Headphones className="h-4 w-4" />;
    case 'asset-video':
      return <Video className="h-4 w-4" />;
    case 'asset-image':
      return <ImageIcon className="h-4 w-4" />;
    case 'asset-audio':
      return <Music className="h-4 w-4" />;
    case 'plan-activity':
      return <Activity className="h-4 w-4" />;
    case 'admin-my-profile':
      return <IdCard className="h-4 w-4" />;
    case 'plan-activity-groups':
      return <UserCog className="h-4 w-4" />;
    case 'admin-team':
      return <Users className="h-4 w-4" />;
    case 'admin-review':
      return <LayoutGrid className="h-4 w-4" />;
    case 'content-taxonomy-queue':
    case 'session-audio-queue':
    case 'plan-activity-queue':
      return <Inbox className="h-4 w-4" />;
    case 'admin-activity-log':
      return <ClipboardList className="h-4 w-4" />;
    case 'admin-notifications':
      return <Bell className="h-4 w-4" />;
    case 'admin-notification-framework':
      return <BellRing className="h-4 w-4" />;
    case 'admin-app-versions':
      return <Smartphone className="h-4 w-4" />;
    case 'admin-cache':
      return <DatabaseZap className="h-4 w-4" />;
    case 'admin-assessments':
      return <ClipboardList className="h-4 w-4" />;
    case 'cv-placement-tester':
      return <ScanFace className="h-4 w-4" />;
    case 'device-fleet':
      return <Cpu className="h-4 w-4" />;
    case 'device-links':
      return <Link2 className="h-4 w-4" />;
    case 'firmware-releases':
      return <Package className="h-4 w-4" />;
    case 'firmware-queue':
      return <Inbox className="h-4 w-4" />;
    case 'firmware-logs':
      return <ClipboardList className="h-4 w-4" />;
    case 'firmware-rollbacks':
      return <RotateCcw className="h-4 w-4" />;
    case 'subscription-entitlements':
      return <BadgeCheck className="h-4 w-4" />;
    case 'subscription-plans':
      return <CreditCard className="h-4 w-4" />;
    case 'support-console':
      return <MessagesSquare className="h-4 w-4" />;
    case 'coach-console':
      return <MessagesSquare className="h-4 w-4" />;
    case 'coach-caseload':
      return <Gauge className="h-4 w-4" />;
    case 'coach-directory':
      return <Users className="h-4 w-4" />;
    case 'coach-quality':
      return <ShieldCheck className="h-4 w-4" />;
    case 'coach-assignment':
      return <UserRoundCog className="h-4 w-4" />;
    case 'ops-now':
      return <Gauge className="h-4 w-4" />;
    case 'ops-team':
      return <Users className="h-4 w-4" />;
    case 'ops-issues':
      return <TriangleAlert className="h-4 w-4" />;
    case 'ops-macros':
      return <BookText className="h-4 w-4" />;
    case 'ops-conversations':
      return <MessageSquareText className="h-4 w-4" />;
    case 'customers-panel':
      return <UserSearch className="h-4 w-4" />;
    case 'coach-insights':
      return <LineChart className="h-4 w-4" />;
    case 'biosignal-syncs':
      return <FileDown className="h-4 w-4" />;
    case 'biosignal-observations':
      return <ScanSearch className="h-4 w-4" />;
    case 'biosignal-metrics':
      return <HeartPulse className="h-4 w-4" />;
    case 'production-line':
      return <Gauge className="h-4 w-4" />;
    default:
      return null;
  }
};

// Sidebar counts come from GET /admin/review/summary/, polled while the tab is visible.
const REVIEW_SUMMARY_POLL_MS = 60000;

const buildNav = ({
  counts,
  canDevices,
  canContent,
  canFirmware,
  canFirmwareManage,
  canNotifications,
  canAppVersions,
  canCache,
  canAssessments,
  canEntitlements,
  canPlans,
  canOverrides,
  canSupport,
  canOpsConsole,
  canCoachChat,
  canCoaches,
  canCustomers,
  canCoachInsights,
  canBiosignal,
  canProduction,
}) => {
  const nav = [
    // Both groups hit content-role endpoints, which reject writes only after the form is filled.
    ...(canContent
      ? [
          {
            id: 'content',
            label: 'Content',
            icon: <LayoutGrid className="h-4 w-4" />,
            items: [
              { id: 'content-taxonomy', label: 'Taxonomy', path: paths.dashboard.content.taxonomy },
              {
                id: 'content-taxonomy-queue',
                label: 'Review queue',
                path: paths.dashboard.content.taxonomyQueue,
                count: counts.taxonomy_node,
              },
              {
                id: 'content-attributes',
                label: 'Attributes',
                path: paths.dashboard.content.attributes,
              },
            ],
          },
          {
            id: 'session',
            label: 'Session',
            icon: <Radio className="h-4 w-4" />,
            items: [
              { id: 'session-audio', label: 'Audio', path: paths.dashboard.session.audio.root },
              {
                id: 'session-audio-queue',
                label: 'Review queue',
                path: paths.dashboard.session.audio.queue,
                count: counts.audio,
              },
            ],
          },
        ]
      : []),
    {
      id: 'asset-manager',
      label: 'Asset Manager',
      icon: <FolderOpen className="h-4 w-4" />,
      items: [
        { id: 'asset-video', label: 'Video', path: paths.dashboard.videos },
        { id: 'asset-image', label: 'Image', path: paths.dashboard.images },
        { id: 'asset-audio', label: 'Audio', path: paths.dashboard.audio },
      ],
    },
    {
      id: 'plan',
      label: 'Plan',
      icon: <CalendarCheck className="h-4 w-4" />,
      items: [
        { id: 'plan-activity', label: 'Activity', path: paths.dashboard.plan.activity.root },
        {
          id: 'plan-activity-queue',
          label: 'Review queue',
          path: paths.dashboard.plan.activity.queue,
          count: counts.activity,
        },
        ...(canOverrides
          ? [
              {
                id: 'plan-activity-groups',
                label: 'Activity groups',
                path: paths.dashboard.plan.activity.groups,
              },
            ]
          : []),
      ],
    },
    {
      id: 'cv-pipeline',
      label: 'CV Pipeline',
      icon: <FlaskConical className="h-4 w-4" />,
      items: [
        {
          id: 'cv-placement-tester',
          label: 'Placement Tester',
          path: paths.dashboard.cv.placementTester,
        },
      ],
    },
  ];

  const deviceItems = [];
  if (canDevices) {
    deviceItems.push({ id: 'device-fleet', label: 'Fleet', path: paths.dashboard.devices.root });
    deviceItems.push({
      id: 'device-links',
      label: 'User links',
      path: paths.dashboard.devices.links,
    });
  }
  if (canFirmware) {
    deviceItems.push({
      id: 'firmware-releases',
      label: 'Firmware releases',
      path: paths.dashboard.firmware.root,
    });
  }
  if (canFirmwareManage) {
    deviceItems.push({
      id: 'firmware-queue',
      label: 'Review queue',
      path: paths.dashboard.firmware.queue,
      count: (counts.firmware || 0) + (counts.rollback_request || 0),
    });
    deviceItems.push({
      id: 'firmware-logs',
      label: 'Update logs',
      path: paths.dashboard.firmware.logs,
    });
    deviceItems.push({
      id: 'firmware-rollbacks',
      label: 'Rollback requests',
      path: paths.dashboard.firmware.rollbacks,
    });
  }
  if (deviceItems.length) {
    nav.push({
      id: 'devices',
      label: 'Devices',
      icon: <Cpu className="h-4 w-4" />,
      items: deviceItems,
    });
  }

  const subscriptionItems = [];
  if (canEntitlements) {
    subscriptionItems.push({
      id: 'subscription-entitlements',
      label: 'Entitlements',
      path: paths.dashboard.subscriptions.entitlements,
    });
  }
  if (canPlans) {
    subscriptionItems.push({
      id: 'subscription-plans',
      label: 'Plans',
      path: paths.dashboard.subscriptions.plans,
    });
  }
  if (subscriptionItems.length) {
    nav.push({
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: <CreditCard className="h-4 w-4" />,
      items: subscriptionItems,
    });
  }

  // Coach-related items, in the coach panel's own tab order; Insights leads.
  const coachItems = [];
  if (canCoachInsights) {
    coachItems.push({
      id: 'coach-insights',
      label: 'Insights',
      path: paths.dashboard.insights.root,
    });
  }
  if (canCoachChat) {
    coachItems.push({
      id: 'coach-console',
      label: 'Sleep Support',
      path: paths.dashboard.coach.console,
    });
  }
  // Coach administration is admin-only; the endpoints 403 for a coach or support agent.
  if (canCoaches) {
    coachItems.push({
      id: 'coach-caseload',
      label: 'Caseload health',
      path: paths.dashboard.coach.caseload,
    });
    coachItems.push({
      id: 'coach-directory',
      label: 'Coaches',
      path: paths.dashboard.coach.directory,
    });
    coachItems.push({
      id: 'coach-quality',
      label: 'Quality review',
      path: paths.dashboard.coach.quality,
    });
    coachItems.push({
      id: 'coach-assignment',
      label: 'Assignment',
      path: paths.dashboard.coach.assignment,
    });
  }

  if (coachItems.length) {
    nav.push({
      id: 'coach',
      label: 'Coach',
      icon: <HeartPulse className="h-4 w-4" />,
      items: coachItems,
    });
  }

  const supportItems = [];
  if (canSupport) {
    supportItems.push({
      id: 'support-console',
      label: 'Agent console',
      path: paths.dashboard.support.console,
    });
  }
  // The console is where an agent works; the Ops screens manage the queue, the team
  // and the answer library - and they run on admin-only endpoints.
  if (canOpsConsole) {
    supportItems.push({ id: 'ops-now', label: 'Ops now', path: paths.dashboard.ops.now });
    supportItems.push({ id: 'ops-team', label: 'Team', path: paths.dashboard.ops.team });
    supportItems.push({ id: 'ops-issues', label: 'Issues', path: paths.dashboard.ops.issues });
    supportItems.push({ id: 'ops-macros', label: 'Macros', path: paths.dashboard.ops.macros });
    supportItems.push({
      id: 'ops-conversations',
      label: 'Conversations',
      path: paths.dashboard.ops.conversations,
    });
  }
  if (supportItems.length) {
    nav.push({
      id: 'support',
      label: 'Support',
      icon: <Headset className="h-4 w-4" />,
      items: supportItems,
    });
  }

  if (canCustomers) {
    nav.push({
      id: 'customers',
      label: 'Customers',
      icon: <Users className="h-4 w-4" />,
      items: [
        { id: 'customers-panel', label: 'Customer Panel', path: paths.dashboard.customers.root },
      ],
    });
  }

  if (canBiosignal) {
    nav.push({
      id: 'biosignal',
      label: 'Biosignal',
      icon: <Waves className="h-4 w-4" />,
      items: [
        { id: 'biosignal-syncs', label: 'Device Syncs', path: paths.dashboard.biosignal.root },
        {
          id: 'biosignal-observations',
          label: 'Observations',
          path: paths.dashboard.biosignal.observations,
        },
        {
          id: 'biosignal-metrics',
          label: 'Night Metrics',
          path: paths.dashboard.biosignal.metrics,
        },
      ],
    });
  }

  // The factory line's dashboard; it opens in its own panel shell like the consoles.
  if (canProduction) {
    nav.push({
      id: 'production',
      label: 'Production',
      icon: <Factory className="h-4 w-4" />,
      items: [{ id: 'production-line', label: 'Line', path: paths.dashboard.production.root }],
    });
  }

  const adminItems = [
    // Every staff member has a card of their own, whatever their role.
    { id: 'admin-my-profile', label: 'My profile', path: paths.dashboard.myProfile },
    { id: 'admin-team', label: 'Team', path: paths.dashboard.team },
  ];
  // The overview reads content endpoints; other roles would only see errors.
  if (canContent) {
    adminItems.push({ id: 'admin-review', label: 'Review overview', path: paths.dashboard.review });
  }
  adminItems.push({
    id: 'admin-activity-log',
    label: 'Activity log',
    path: paths.dashboard.auditLog,
  });
  if (canNotifications) {
    adminItems.push({
      id: 'admin-notifications',
      label: 'Notifications',
      path: paths.dashboard.notifications.root,
    });
    adminItems.push({
      id: 'admin-notification-framework',
      label: 'Notification framework',
      path: paths.dashboard.notifications.framework.root,
    });
  }
  if (canAppVersions) {
    adminItems.push({
      id: 'admin-app-versions',
      label: 'App versions',
      path: paths.dashboard.appVersions,
    });
  }
  if (canCache) {
    adminItems.push({ id: 'admin-cache', label: 'Cache', path: paths.dashboard.cache });
  }
  if (canAssessments) {
    adminItems.push({
      id: 'admin-assessments',
      label: 'Assessments',
      path: paths.dashboard.assessments,
    });
  }
  nav.push({
    id: 'administration',
    label: 'Administration',
    icon: <Users className="h-4 w-4" />,
    items: adminItems,
  });
  return nav;
};

const SidebarItem = ({ item, active }) => (
  <Link
    to={item.path}
    onFocus={() => preloadRoute(item.path)}
    onMouseEnter={() => preloadRoute(item.path)}
    className={cn(
      'group flex h-9 items-center gap-2 rounded px-2 text-base lg:h-8',
      active ? 'bg-nav-item-select text-foreground' : 'text-text-default hover:bg-nav-item-hover'
    )}
    data-status={active ? 'active' : undefined}
  >
    <span className="text-icon-color">{iconFor(item.id)}</span>
    <span className="truncate leading-[1.3]">{item.label}</span>
    {item.count > 0 && (
      <Badge variant="warning" className="ml-auto px-1.5 text-xs">
        {item.count}
      </Badge>
    )}
  </Link>
);

const SidebarGroup = ({ group, pathname }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 items-center justify-between px-2 text-sm font-medium text-gray-dark hover:text-foreground"
      >
        <span className="flex items-center gap-2">
          <span className="text-icon-color">{group.icon}</span>
          <span className="uppercase tracking-wide">{group.label}</span>
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', open ? 'rotate-0' : '-rotate-90')}
        />
      </button>
      {open && (
        <div className="mt-1 flex flex-col gap-0.5">
          {group.items.map((item) => {
            const active = pathname === item.path || pathname.startsWith(item.path + '/');
            return <SidebarItem key={item.id} item={item} active={active} />;
          })}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ open = false, onClose }) => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [aboutOpen, setAboutOpen] = useState(false);
  const user = useAppSelector(selectUser);
  const counts = useAppSelector(selectReviewSummary);
  const canContent = canReadContent(user);
  const canFirmware = canReadFirmware(user);

  useEffect(() => {
    if (!canContent && !canFirmware) return undefined;
    const poll = () => {
      if (document.visibilityState === 'visible') dispatch(getReviewSummaryAction());
    };
    poll();
    const timer = setInterval(poll, REVIEW_SUMMARY_POLL_MS);
    return () => clearInterval(timer);
  }, [dispatch, canContent, canFirmware]);

  const nav = useMemo(
    () =>
      buildNav({
        counts,
        canDevices: canManageDevices(user),
        canContent,
        canFirmware,
        canFirmwareManage: canManageFirmware(user),
        canNotifications: canManageNotifications(user),
        canAppVersions: canManageAppVersions(user),
        canCache: canManageCache(user),
        canAssessments: canManageAssessments(user),
        canEntitlements: canManageEntitlements(user),
        canPlans: canManagePlans(user),
        canOverrides: canManageActivityGroups(user),
        canSupport: canWorkSupportChat(user),
        canOpsConsole: canRunSupportConsole(user),
        canCoachChat: canWorkCoachChat(user),
        canCoaches: canManageSleepCoaches(user),
        canCustomers: canViewCustomers(user),
        canCoachInsights: canViewCoachInsights(user),
        canBiosignal: canViewBiosignal(user),
        canProduction: canViewProduction(user),
      }),
    [user, counts, canContent, canFirmware]
  );

  // Close the mobile drawer after navigating.
  useEffect(() => {
    onClose?.();
  }, [pathname, onClose]);

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/60 animate-fade-in lg:hidden"
        />
      )}
      <aside
        data-tour="sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-full w-[240px] shrink-0 flex-col border-r border-border bg-background transition-transform duration-normal lg:static lg:z-auto lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Dusq"
            className="h-8 w-8 rounded-md object-cover shadow"
          />
          <div className="flex flex-col gap-0.5 leading-none">
            <span className="font-[family-name:var(--font-heading)] text-md text-foreground">
              Dusq
            </span>
            <span className="text-xs text-gray-dark">Admin panel</span>
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="-mr-2 ml-auto flex h-11 w-11 items-center justify-center rounded-full text-icon-color hover:bg-item-hover lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
          {nav.map((group) => (
            <SidebarGroup key={group.id} group={group} pathname={pathname} />
          ))}
        </nav>
        <div className="shrink-0 border-t border-border px-3 py-3">
          <button
            type="button"
            onClick={() => setAboutOpen(true)}
            className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-dark hover:bg-item-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>Version {APP_VERSION}</span>
          </button>
          <a
            href={supportMailto(`Dusq Admin ${APP_VERSION}`)}
            className="block truncate px-2 py-1 text-xs text-gray-light hover:text-foreground"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </aside>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </>
  );
};
